import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useIsWeb } from '@/hooks/useIsWeb';
import type { ChangeEvent } from 'react';
import { SizableText, XStack, YStack } from 'tamagui';
import { FieldTitle } from '@components/FieldTitle/FieldTitle';
import { StyledTextArea } from './textArea.styled';

interface TextAreaFieldProps {
  /** Field label */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Current value */
  value: string;
  /** Callback when value changes */
  onValueChange: (value: string) => void;
  /** Maximum number of characters allowed */
  maxLength?: number;
  /** Whether this field is required */
  required?: boolean;
}

/**
 * TextAreaField - Multi-line text input field
 *
 * Features:
 * - Multi-line text input
 * - Character count display (bottom-right corner)
 * - Max length enforcement
 * - Consistent styling with other form fields
 */
export const TextAreaField = ({
  label,
  placeholder,
  value,
  onValueChange,
  maxLength = 5000,
  required = false,
}: TextAreaFieldProps): React.ReactElement => {
  const isWeb = useIsWeb();

  const handleNativeChangeText = (text: string): void => {
    onValueChange(text);
  };

  const handleWebChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    onValueChange(event.target?.value ?? '');
  };

  const { t } = useAppTranslation();
  const currentLength = value?.length || 0;
  const characterLimitLabel = t('reportCreation.common.characterLimit', {
    currentLength,
    maxLength,
  });

  return (
    <YStack gap="$1">
      {label && <FieldTitle label={label} required={required} />}
      <YStack position="relative">
        <StyledTextArea
          value={value}
          onChangeText={isWeb ? undefined : handleNativeChangeText}
          onChange={isWeb ? handleWebChange : undefined}
          placeholder={placeholder}
          maxLength={maxLength}
          multiline
          numberOfLines={4}
          textAlignVertical="top"
        />
        <XStack position="absolute" bottom="$2" right="$2" borderRadius="$2">
          <SizableText size="$3" color="$secondary75">
            {characterLimitLabel}
          </SizableText>
        </XStack>
      </YStack>
    </YStack>
  );
};
